import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  FormControl,
  HStack,
  Icon,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { FaInfoCircle } from 'react-icons/fa';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import { Card } from '~/components/Card';
import { Input, Table } from '~/components';
import {
  TerminationRequestsContractView,
  TerminationRequestsEquipament,
} from '~/types/terminationRequests';
import { replaceParameters } from '~/utils/replaceParamaters';
import { ROUTER_PATHS } from '~/constants';

export const SelectEquipmentCard: React.VFC<{
  contract: TerminationRequestsContractView;
  quantidadeParcial: (
    qtde: number,
    contrato: TerminationRequestsContractView,
    equipamento: TerminationRequestsEquipament,
  ) => void;
  selecionar: (equipamentos: TerminationRequestsEquipament[]) => void;
  visualizacao: boolean;
  analisando?: boolean;
}> = ({
  contract,
  quantidadeParcial,
  selecionar,
  visualizacao,
  analisando,
}) => {
  const history = useHistory();
  const [todosSelecionados, setTodosSelecionados] = useState(false);
  const { setError, clearErrors, formState } = useForm();
  const { errors } = formState;

  const selecionarTodos = () => {
    const todos = contract.equipamentos.map(it => {
      return {
        ...it,
        selecionado: !todosSelecionados,
      };
    });
    setTodosSelecionados(!todosSelecionados);
    selecionar(todos);
  };

  function handleQuantidade(e: any, eq: TerminationRequestsEquipament) {
    clearErrors(`qtde_parcial-${eq.id_equipamento}`);
    if (!Number(e)) {
      setError(`qtde_parcial-${eq.id_equipamento}`, {
        type: 'required',
        message: 'A quantidade deve ser maior que 0',
      });
    }
    if (e.length > 12) {
      return;
    }
    const value = e.replace(/\D/g, '');
    if (Number(value) > eq.qtde_total) {
      return;
    }
    quantidadeParcial(Number(value), contract, eq);
  }

  function selecionarEquipamento(eq: TerminationRequestsEquipament) {
    const seleciona = contract.equipamentos.map(it => {
      if (it.cd_equipamento === eq.cd_equipamento) {
        return {
          ...it,
          selecionado: !it.selecionado,
        };
      }
      return it;
    });
    const todos = seleciona.every(it => it.selecionado);
    setTodosSelecionados(todos);
    selecionar(seleciona);
  }

  const totalQuantidade = contract.equipamentos.reduce(
    (acc, it) => acc + Number(it.qtde_total),
    0,
  );

  const totalQuantidadeParcial =
    !visualizacao && !analisando
      ? contract.equipamentos.reduce(
          (acc, it) =>
            it.selecionado
              ? acc + Number(it.qtde_parcial ?? it.qtde_total)
              : acc,
          0,
        )
      : contract.equipamentos.reduce(
          (acc, it) => acc + Number(it.qtde_parcial ?? it.qtde_total),
          0,
        );

  const header = () => {
    return (
      <HStack px={3} justifyContent="space-between">
        <HStack>
          <Text fontWeight="bold">Contrato</Text>
          <Button
            variant="link"
            fontWeight="bold"
            color="#5b8af0"
            _hover={{
              color: 'gray.900',
              textDecoration: 'underline',
            }}
            onClick={() =>
              history.push(
                replaceParameters(
                  ROUTER_PATHS.PORTAL.CONCTRACT_DETAIL,
                  String(contract.id_contrato),
                ),
              )
            }
            fontSize="15px"
            textDecoration="underline"
          >
            {contract.nu_contrato}
          </Button>
        </HStack>
      </HStack>
    );
  };

  const content = () => {
    return (
      <Stack>
        {!visualizacao && (
          <Text fontWeight="bold" fontSize="sm">
            Selecione todos os equipamentos para rescisão ou informe a
            quantidade parcial:
          </Text>
        )}
        <Table type="secondary" maxW="70vw" w="70vw" bgColor="white">
          <Thead>
            <Tr>
              {!visualizacao && !analisando && (
                <Th>
                  <Checkbox
                    colorScheme="b"
                    isChecked={todosSelecionados}
                    onChange={() => {
                      selecionarTodos();
                    }}
                    fontSize="sm"
                  />
                </Th>
              )}
              <Th>Código</Th>
              <Th>Tipo</Th>
              <Th>Modelo</Th>
              <Th>Descrição</Th>
              <Th>Quantidade</Th>
              <Th textAlign="right">
                Rescisão parcial{' '}
                <Tooltip
                  hasArrow
                  label={
                    !visualizacao
                      ? 'Informar a quantidade desejada para rescisão parcial.'
                      : ''
                  }
                  bg="black"
                  rounded="md"
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon as={FaInfoCircle} boxSize={4} color="gray.900" />
                </Tooltip>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {contract.equipamentos.length ? (
              contract.equipamentos.map(it => (
                <Tr key={it.id_equipamento}>
                  {!visualizacao && !analisando && (
                    <Td>
                      <Checkbox
                        colorScheme="b"
                        isChecked={it.selecionado}
                        onChange={() => {
                          selecionarEquipamento(it);
                        }}
                        fontSize="sm"
                      />
                    </Td>
                  )}
                  <Td>{it.cd_equipamento || '-'}</Td>
                  <Td>{it.nm_tipoequipamento || '-'}</Td>
                  <Td>{it.nm_modeloequipamento || '-'}</Td>
                  <Td>{it.ds_equipamento || '-'}</Td>
                  <Td>{it.qtde_total || '-'}</Td>
                  <Td maxW="200px" w="200px">
                    <FormControl
                      isInvalid={errors[`qtde_parcial-${it.id_equipamento}`]}
                    >
                      <Input
                        error={errors[`qtde_parcial-${it.id_equipamento}`]}
                        type="text"
                        size="sm"
                        textAlign="right"
                        fontSize="15px"
                        maxLength={12}
                        value={Number(it.qtde_parcial).toLocaleString('pt-BR', {
                          maximumFractionDigits: 0,
                        })}
                        name="quantidade"
                        onChange={e => handleQuantidade(e.target.value, it)}
                        isDisabled={visualizacao || analisando}
                      />
                    </FormControl>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={7}>
                  <Alert status="info" variant="subtle" colorScheme="purple">
                    <AlertTitle mr={2} fontSize="sm">
                      Nenhum equipamento encontrado
                    </AlertTitle>
                    <AlertDescription fontSize="sm">
                      Nenhum equipamento encontrado para o contrato selecionado.
                    </AlertDescription>
                  </Alert>
                </Td>
              </Tr>
            )}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th
                colSpan={!visualizacao && !analisando ? 5 : 4}
                textAlign="right"
                fontWeight="bold"
              >
                Total de equipamentos
              </Th>
              <Th fontWeight="bold">{totalQuantidade}</Th>
              <Th fontWeight="bold" textAlign="right">
                {totalQuantidadeParcial}
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </Stack>
    );
  };

  return (
    <Card
      key="a"
      header={header()}
      content={content()}
      width="fit-content"
      minW="fit-content"
      bg="transparent"
      borderTopColor="#9246ce"
      backgroundColor="#fbfafe"
    />
  );
};
